import { useState, useCallback } from "react";
import { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid-premium";

interface ContextMenuState {
  open: boolean;
  event: MouseEvent | null;
  selectedRows: any[];
}

interface UseRowSelectionOptions {
  apiRef: React.RefObject<any>;
  ContextMenuComponent?: React.ComponentType<any>;
}

interface UseRowSelectionReturn {
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>;
  contextMenuState: ContextMenuState;
  handleRowSelectionModelChange: (newSelection: GridRowSelectionModel) => void;
  handleContextMenu: (event: React.MouseEvent) => void;
  closeContextMenu: () => void;
  getSelectedRows: () => any[];
}

/**
 * Hook for managing row selection state, context menu, and selection normalization.
 */
export function useRowSelection({
  apiRef,
  ContextMenuComponent,
}: UseRowSelectionOptions): UseRowSelectionReturn {
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set<GridRowId>(),
    });

  const [contextMenuState, setContextMenuState] = useState<ContextMenuState>({
    open: false,
    event: null,
    selectedRows: [],
  });

  /**
   * Converts an "exclude" selection model (select all) to an "include" model
   * containing only the currently visible page rows.
   */
  const normalizeSelectionToCurrentPage = useCallback(
    (selection: GridRowSelectionModel): GridRowSelectionModel => {
      // If it's "exclude" mode with empty ids, user clicked "select all"
      // Convert to "include" mode with only current page rows
      if (selection.type === "exclude" && selection.ids.size === 0) {
        const visibleRowIds = new Set<GridRowId>();

        // getRowModels() returns only the currently loaded/visible rows
        const rowModels = apiRef.current?.getRowModels();
        if (rowModels) {
          rowModels.forEach((row: any, id: GridRowId) => {
            // Skip group rows (they have __groupKey__ property)
            if (row.__groupKey__ === undefined) {
              visibleRowIds.add(id);
            }
          });
        }

        return {
          type: "include",
          ids: visibleRowIds,
        };
      }

      return selection;
    },
    [apiRef]
  );

  const handleRowSelectionModelChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      const normalizedSelection = normalizeSelectionToCurrentPage(newSelection);
      setRowSelectionModel(normalizedSelection);
    },
    [normalizeSelectionToCurrentPage]
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (!ContextMenuComponent) return;

      event.preventDefault();

      // Get currently selected rows
      const selectedIds = Array.from(rowSelectionModel.ids);
      let rowsForContextMenu: any[] = [];

      if (selectedIds.length > 0) {
        // Use existing selection
        rowsForContextMenu = selectedIds
          .map((id) => apiRef.current?.getRow(id))
          .filter(Boolean);
      } else {
        // Find the clicked row
        const target = event.target as HTMLElement;
        const rowElement = target.closest('[role="row"]');

        if (rowElement) {
          const rowId = rowElement.getAttribute("data-id");
          if (rowId) {
            const clickedRow = apiRef.current?.getRow(rowId);
            if (clickedRow) {
              rowsForContextMenu = [clickedRow];
            }
          }
        }
      }

      if (rowsForContextMenu.length > 0) {
        setContextMenuState({
          open: true,
          event: event.nativeEvent,
          selectedRows: rowsForContextMenu,
        });
      }
    },
    [ContextMenuComponent, rowSelectionModel.ids, apiRef]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenuState({
      open: false,
      event: null,
      selectedRows: [],
    });
  }, []);

  // Get complete row objects for all selected rows
  const getSelectedRows = useCallback(() => {
    const selectedIds = Array.from(rowSelectionModel.ids);
    return selectedIds
      .map((id) => ({ id, ...apiRef.current?.getRow(id) }))
      .filter(Boolean);
  }, [rowSelectionModel.ids, apiRef]);

  return {
    rowSelectionModel,
    setRowSelectionModel,
    contextMenuState,
    handleRowSelectionModelChange,
    handleContextMenu,
    closeContextMenu,
    getSelectedRows,
  };
}
